import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { TokenService } from '../token/token.service';
import { ProfileImg } from './profile-img';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  businessId: any;
  profileInfoChange = new Subject();
  private businessInfo$: Promise<any> | undefined;

  constructor(
    private config: ConfigService,
    private http: HttpClient,
    private tokenServe: TokenService,
  ) {
    this.businessId = this.tokenServe.getBusinessId();
  }

  getProfile(userId: string): Promise<any> {
    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/userProfile/${userId}`));
  }

  getBusinessDetails({ cache } = { cache: false }): Promise<any> {
    if (!this.businessInfo$ || !cache) {
      this.businessInfo$ = lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/getBusiness/${this.businessId}`));
    }

    return this.businessInfo$;
  }

  getBrandCategories(): Promise<any> {
    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/categories`));
  }

  updateProfileData(id: any, data: any): Promise<any> {
    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/brandbusiness/userProfile/${id}`, data))
  }

  async checkIfMobileNumberAlreadyExists(mobile: any): Promise<any> {
    return this.http.get(`${this.config.API_BASE}/api/v1/customer/userProfile/validatemobile/${mobile}`);
  }

  async generateOTP(mobileObj: any): Promise<any> {
    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/otp/generate`, mobileObj));
  }

  async validateOTP(mobileObj: any): Promise<any> {
    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/otp/validate`, mobileObj));
  }

  createBrand(data: any): Promise<any> {
    const reqData = { ...data, businessProfile: { businessId: this.businessId } };
    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/brandbusiness`, reqData));
  }

  // OnBoard Methods
  createBrandStep1(data: any,): Promise<any> {
    const reqData = { ...data, stepNo: 1, businessProfile: { businessId: this.businessId } };
    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/brandbusiness`, reqData));
  }

  async updateStep1(brandId: string, data: any): Promise<any> {
    const step1Data = await lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/${brandId}`));
    const nData = { ...step1Data, ...data, stepNo: 1 };
    return lastValueFrom(this.http.put(`${this.config.API_BASE}/api/v1/brandbusiness/${brandId}`, nData));
  }

  getStep1Data(): Promise<any> {
    const brandId: string = this.tokenServe.getBrands()[0]?.id;
    if (!brandId) {
      console.warn('Brand ID does not exist in login info');
      return Promise.reject();
    }

    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/${brandId}`));
  }

  async updateStep2(brandId: string, data: any): Promise<any> {
    const step1Data = await lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/${brandId}`));
    // console.log(step1Data);
    const nData = { ...step1Data, ...data, stepNo: 2 };

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/brandbusiness`, nData));
  }

  // brand info apis

  getBrandsByBusiness(): Promise<any> {
    const url = `${this.config.API_BASE}/api/v1/brandbusiness/getBusinessBrands/${this.businessId}`
    return lastValueFrom(this.http.get(url));
  }

  async updateBrandById(brandId: string, data: any): Promise<any> {
    const step1Data = await lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/${brandId}`));
    // console.log(step1Data);
    const nData = { ...step1Data, ...data };

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/brandbusiness`, nData));
  }

  getBrandById(brandId: string): Promise<any> {
    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/${brandId}`));
  }

  // Password Update

  updatePassword(data: any): Promise<any> {
    return lastValueFrom(this.http.put(`${this.config.API_BASE}/api/auth/updatePassword`, data))
  }

  uploadBusinessImageFiles(file: File, key: ProfileImg, businessId?: string) {
    const data = new FormData();
    data.append('file', file);
    data.append('assetClassificationTypeEnum', key);
    if (businessId) {
      data.append('businessId', businessId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common`, data));
  }

  uploadProfileRelatedImageFiles(file: File, key: ProfileImg, brandId?: string) {
    const data = new FormData();
    data.append('file', file);
    data.append('assetClassificationTypeEnum', key);
    if (brandId) {
      data.append('brandId', brandId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common`, data));
  }

  /*
  uploadBannerImageFiles(file: File, brandId?: string) {
    const data = new FormData();
    data.append('file', file);
    data.append('assetClassificationTypeEnum', ProfileImg.BRAND_ASSETS_BANNER_IMAGES);
    if (brandId) {
      data.append('brandId', brandId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common`, data));
  }
  */

  uploadBannerImageFiles(file: File, key: ProfileImg, brandId?: string, sequenceNo?: number) {
    const data = new FormData();
    data.append('file', file);
    data.append('assetClassificationTypeEnum',key);
    data.append('sequenceNo', String(sequenceNo));
    if (brandId) {
      data.append('brandId', brandId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common`, data));
  }


  newUploadMethod(data: FormData, { storeId, brandId }: any) {
    if (storeId) {
      data.append('storeId', storeId);
    }

    if (brandId) {
      data.append('brandId', brandId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common/multipule2`, data));
  }

  uploadFounderImageFiles(file: File, key: ProfileImg, brandId?: string, sequenceNo?: number) {
    const data = new FormData();
    data.append('file', file);
    data.append('assetClassificationTypeEnum',key);
    data.append('sequenceNo', String(sequenceNo));
    if (brandId) {
      data.append('brandId', brandId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common`, data));
  }

  /*uploadFounderImageFiles(file: File, brandId?: string) {
    const data = new FormData();
    data.append('file', file);
    data.append('assetClassificationTypeEnum', ProfileImg.BRAND_ASSETS_FOUNDER_LOGO);
    if (brandId) {
      data.append('brandId', brandId);
    }

    return lastValueFrom(this.http.post(`${this.config.API_BASE}/api/v1/assets/upload/common`, data));
  }*/

  // Profile Image
  getImageForProfileSection(urlPath: string): Promise<any> {
    // const url = `${this.config.API_BASE}${urlPath}`;
    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/assets/upload${urlPath}`, { responseType: 'blob' }));
  }

  async updateBusinessData(data: any): Promise<any> {
    return lastValueFrom(this.http.put(`${this.config.API_BASE}/api/v1/brandbusiness/updateBusiness/${this.businessId}`, data));
  }

  async cancelBrand(id: string): Promise<any> {
    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/cancelBrand/${id}`));
  }


  async activateBrand(id: string): Promise<any> {
    return lastValueFrom(this.http.get(`${this.config.API_BASE}/api/v1/brandbusiness/activateBrand/${id}`));

  }
}
